import PageContainer from "@/Components/Navigation/PageContainer";
import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import { homePageFor } from "@/utils/homePage";
import { useIdentity } from "@/utils/identity";
import { navigateTo, usePageParams } from "@/utils/navigation";

const ERROR_CODE_WHEN_MISSING = 500;

export default function ErrorPage() {
    const { errorCode } = usePageParams();

    return (
        <ErrorScreen
            errorCode={errorCode ? Number(errorCode) : ERROR_CODE_WHEN_MISSING}
        />
    );
}

type ErrorScreenProps = {
    errorCode: number;
};

export function ErrorScreen({ errorCode }: ErrorScreenProps) {
    const { user } = useIdentity();
    const home = homePageFor(user);

    const errData = {
        503: {
            text: "Sorry, we are doing some maintenance. Please check back soon.",
            image: (
                <img
                    src={`${import.meta.env.VITE_CDN_URL}/images/error_503.webp`}
                    alt="strawberry juice machine under maintenance"
                    className="mx-auto w-full sm:w-1/4"
                />
            ),
        },
        500: {
            text: "Whoops, something went wrong on our servers.",
            image: (
                <img
                    src={`${import.meta.env.VITE_CDN_URL}/images/error_500.webp`}
                    alt="broken strawberry robot"
                    className="mx-auto w-full sm:w-1/4"
                />
            ),
        },
        404: {
            text: "Sorry, the page you are looking for could not be found.",
            image: (
                <img
                    src={`${import.meta.env.VITE_CDN_URL}/images/error_404.webp`}
                    alt="lost strawberry astronaut"
                    className="mx-auto w-full sm:w-1/4"
                />
            ),
        },
        403: {
            text: "Sorry, you are forbidden from accessing this page.",
            image: (
                <img
                    src={`${import.meta.env.VITE_CDN_URL}/images/error_403.webp`}
                    alt="locked strawberry book"
                    className="mx-auto w-full sm:w-1/4"
                />
            ),
        },
        429: {
            text: "Too many requests. Please wait a little while before trying again.",
            image: (
                <img
                    src={`${import.meta.env.VITE_CDN_URL}/images/error_429_cropped.webp`}
                    alt="locked strawberry book"
                    className="mx-auto w-full sm:w-1/4"
                />
            ),
        },
    }[errorCode];

    return (
        <PageContainer title={errorCode.toString()} showFooter={false}>
            <div className="w-full bg-brand-white px-4">
                {errData?.image}
                <div
                    className="mt-12 text-center text-6xl font-bold text-brand-black"
                    data-error-code={errorCode}
                >
                    {errorCode}
                </div>
                <p className="mt-4 text-center text-brand-black">
                    {errData?.text}
                </p>
                <div className="mt-4 flex justify-center pb-12">
                    <PrimaryButton
                        onClick={() => navigateTo(home)}
                        className="mx-auto w-fit"
                    >
                        Go Home
                    </PrimaryButton>
                </div>
            </div>
        </PageContainer>
    );
}
